import React, { useState } from "react";
import axios from "axios";
import { v4 as uuidv4 } from "uuid";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";

const reverseMatcher = /(_B2CREVERSE_|REVERSE_)/i;

const RateCalculator = () => {
  const navigate = useNavigate();
  const [shipmentType, setShipmentType] = useState("forward"); // "forward" | "reverse"
  const [paymentMode, setPaymentMode] = useState("prepaid");   // "prepaid" | "cod"
  const [formData, setFormData] = useState({
    pickupPincode: "",
    deliveryPincode: "",
    length: "",
    breadth: "",
    height: "",
    weight: "",
    invoiceAmount: "",
  });
  const [units, setUnits] = useState({
    length: "cm",
    breadth: "cm",
    height: "cm",
    weight: "gm",
  });
  const [courierRates, setCourierRates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState("");
  const [sortBy, setSortBy] = useState("price"); // price, edd, partner

  const convertToCm = (value, unit) => {
    const num = parseFloat(value) || 0;
    return unit === "inch" ? num * 2.54 : num;
  };

  const convertToGrams = (value, unit) => {
    const num = parseFloat(value) || 0;
    return unit === "kg" ? num * 1000 : num;
  };

  // Validate form fields
  const validateForm = () => {
    const errors = {};

    if (!formData.pickupPincode || !/^[1-9][0-9]{5}$/.test(formData.pickupPincode)) {
      errors.pickupPincode = "Valid 6-digit pincode required";
    }

    if (!formData.deliveryPincode || !/^[1-9][0-9]{5}$/.test(formData.deliveryPincode)) {
      errors.deliveryPincode = "Valid 6-digit pincode required";
    }

    if (!formData.length || parseFloat(formData.length) <= 0) {
      errors.length = "Length required";
    }

    if (!formData.breadth || parseFloat(formData.breadth) <= 0) {
      errors.breadth = "Breadth required";
    }

    if (!formData.height || parseFloat(formData.height) <= 0) {
      errors.height = "Height required";
    }

    if (!formData.weight || parseFloat(formData.weight) <= 0) {
      errors.weight = "Weight required";
    }

    if (!formData.invoiceAmount || parseFloat(formData.invoiceAmount) <= 0) {
      errors.invoiceAmount = "Invoice amount required";
    }

    return errors;
  };

  // Sort rates
  const sortedRates = [...courierRates].sort((a, b) => {
    if (sortBy === "price") {
      const priceA = parseFloat(a.charges.replace("₹", ""));
      const priceB = parseFloat(b.charges.replace("₹", ""));
      return priceA - priceB;
    } else if (sortBy === "edd") {
      const eddA = parseInt(a.edd);
      const eddB = parseInt(b.edd);
      return eddA - eddB;
    } else {
      return a.partner.localeCompare(b.partner);
    }
  });

  const handleCalculate = async () => {
    // Clear previous errors
    setFieldErrors({});
    setError("");
    setSuccessMessage("");

    // Validate
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setLoading(true);
    setCourierRates([]);

    try {
      // Read from localStorage
      const allocatedCarriersRaw = JSON.parse(localStorage.getItem("allocatedCarriers") || "[]");
      const merchantId = localStorage.getItem("merchantId");
      const authToken = localStorage.getItem("authToken");

      if (!authToken) {
        console.error("Auth token not found in local storage.");
        navigate("/login");
        return;
      }

      // Normalize carriers: support both string and object forms
      const allCarriers = (allocatedCarriersRaw || [])
        .map((c) => (typeof c === "string" ? { carrier_partner_id: c } : c))
        .filter(
          (c) =>
            c &&
            typeof c.carrier_partner_id === "string" &&
            c.carrier_partner_id.trim().length > 0
        );

      console.log("Allocated Carriers (normalized):", allCarriers);

      if (!allCarriers.length) {
        setError("No allocated carriers found. Please try logging in again.");
        setLoading(false);
        return;
      }

      // Filter carriers based on shipment type
      // FORWARD → exclude REVERSE lanes
      // REVERSE → include ONLY REVERSE lanes
      const filteredCarriers =
        shipmentType === "forward"
          ? allCarriers.filter((c) => !reverseMatcher.test(c.carrier_partner_id))
          : allCarriers.filter((c) => reverseMatcher.test(c.carrier_partner_id));

      if (!filteredCarriers.length) {
        setError(
          shipmentType === "forward"
            ? "No forward-enabled carriers found."
            : "No reverse-enabled carriers found."
        );
        setLoading(false);
        return;
      }

      const weightInGrams = convertToGrams(formData.weight, units.weight);
      const lengthInCm = convertToCm(formData.length, units.length);
      const breadthInCm = convertToCm(formData.breadth, units.breadth);
      const heightInCm = convertToCm(formData.height, units.height);

      // Map to API shipment_type
      const shipmentTypeForApi = shipmentType === "forward" ? "FORWARD" : "RVP";

      const requestPayload = filteredCarriers.map((carrier) => ({
        correlation_id: uuidv4(),
        carrier_partner_id: carrier.carrier_partner_id,
        shipment_type: shipmentTypeForApi,
        payment_mode: paymentMode.toUpperCase(), // PREPAID / COD
        invoice_value: parseFloat(formData.invoiceAmount),
        rvp_with_quality_check: false,
        weight_in_grams: weightInGrams,
        length_in_cm: lengthInCm,
        breadth_in_cm: breadthInCm,
        height_in_cm: heightInCm,
        pickup_pincode: formData.pickupPincode,
        drop_pincode: formData.deliveryPincode,
      }));

      console.log("Final Request Payload:", JSON.stringify(requestPayload, null, 2));

      const response = await axios.post(
        `${process.env.REACT_APP_OMS_BASE_URL}${process.env.REACT_APP_MERCHANT_DOMESTIC_PRICING_BATCH}`,
        requestPayload,
        {
          headers: {
            Authorization: authToken,
            "x-merchant-id": merchantId,
            "Content-Type": "application/json",
          },
        }
      );
      console.log("API Response:", response.data);

      // Extra safety filter on response based on REVERSE rules
      const filteredRates = (response.data || []).filter((rate) => {
        const id = rate?.carrier_partner_id || "";
        const isReverseService = reverseMatcher.test(id);

        if (shipmentType === "reverse") {
          return isReverseService;
        }
        return !isReverseService;
      });

      if (!filteredRates.length) {
        setError("No valid carriers found for selected shipment type.");
        setCourierRates([]);
        setLoading(false);
        return;
      }

      // Transform response data to match UI structure
      const formattedRates = filteredRates.map((rate) => {
        const amount = Number(rate.total_amount_charged_after_tax ?? 0);
        return {
          charges: `₹${amount.toFixed(2)}`,
          partner: rate.carrier_partner_public_name,
          mode: shipmentTypeForApi, // FORWARD / RVP
          edd: `${rate.max_tat_in_days} DAYS`,
        };
      });

      setCourierRates(formattedRates);
      setSuccessMessage("Rates calculated successfully!");
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (err) {
      console.error("Error fetching rates:", err);
      setError("Failed to fetch courier rates. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    // Prevent negative values for dimensions and weight
    if (["length", "breadth", "height", "weight", "invoiceAmount"].includes(e.target.name)) {
      if (value !== "" && parseFloat(value) < 0) {
        return; // Don't update state if negative
      }
    }
    setFormData({ ...formData, [e.target.name]: value });
    // Clear field error when user starts typing
    if (fieldErrors[e.target.name]) {
      setFieldErrors({ ...fieldErrors, [e.target.name]: "" });
    }
  };

  const handleUnitChange = (e) => {
    setUnits({ ...units, [e.target.name]: e.target.value });
  };

  const handleShipmentTypeChange = (type) => {
    setShipmentType(type);
    // Auto-switch from COD to Prepaid when selecting reverse
    if (type === "reverse" && paymentMode === "cod") {
      setPaymentMode("prepaid");
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="bg-white shadow-xl sm:shadow-2xl rounded-xl sm:rounded-2xl p-3 sm:p-4 md:p-6 lg:p-8 w-full mx-auto grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8 border border-gray-100"
    >
      <div className="lg:col-span-2">
        {/* Header */}
        <div className="flex items-center justify-between mb-4 sm:mb-6">
          <div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              Shipment Rate Calculator
            </h3>
            <p className="text-xs sm:text-sm text-gray-600">Get instant quotes from multiple carriers</p>
          </div>
          <div className="hidden sm:block">
            <div className="bg-gradient-to-br from-yellow-400 to-orange-500 text-white p-2 sm:p-3 lg:p-4 rounded-lg sm:rounded-xl shadow-lg">
              <span className="text-2xl">💰</span>
            </div>
          </div>
        </div>

        {/* Shipment Type & Payment Mode */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 lg:gap-6 mb-4 sm:mb-6">
          <div>
            <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-2 sm:mb-3">
              Shipment Type <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-2 gap-2 sm:gap-3">
              {["forward", "reverse"].map((type) => (
                <motion.label
                  key={type}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className={`flex items-center justify-center gap-1.5 sm:gap-2 p-3 sm:p-4 rounded-lg sm:rounded-xl border-2 cursor-pointer transition-all min-h-[52px] ${
                    shipmentType === type
                      ? "border-blue-500 bg-blue-50 text-blue-700"
                      : "border-gray-200 hover:border-gray-300 bg-white"
                  }`}
                >
                  <input
                    type="radio"
                    name="shipmentType"
                    value={type}
                    checked={shipmentType === type}
                    onChange={(e) => handleShipmentTypeChange(e.target.value)}
                    className="sr-only"
                  />
                  <span className="text-lg sm:text-xl">{type === "forward" ? "➡️" : "⬅️"}</span>
                  <span className="font-semibold capitalize text-sm sm:text-base">{type}</span>
                </motion.label>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-2 sm:mb-3">
              Payment Mode <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-2 gap-2 sm:gap-3">
              {["prepaid", "cod"].map((mode) => {
                const isDisabled = mode === "cod" && shipmentType === "reverse";
                return (
                  <motion.label
                    key={mode}
                    whileHover={{ scale: isDisabled ? 1 : 1.02 }}
                    whileTap={{ scale: isDisabled ? 1 : 0.98 }}
                    className={`flex items-center justify-center gap-1.5 sm:gap-2 p-3 sm:p-4 rounded-lg sm:rounded-xl border-2 transition-all min-h-[52px] ${
                      isDisabled
                        ? "border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed opacity-60"
                        : paymentMode === mode
                        ? "border-green-500 bg-green-50 text-green-700 cursor-pointer"
                        : "border-gray-200 hover:border-gray-300 bg-white cursor-pointer"
                    }`}
                  >
                    <input
                      type="radio"
                      name="paymentMode"
                      value={mode}
                      checked={paymentMode === mode}
                      onChange={(e) => setPaymentMode(e.target.value)}
                      disabled={isDisabled}
                      className="sr-only"
                    />
                    <span className="text-lg sm:text-xl">{mode === "prepaid" ? "💳" : "💵"}</span>
                    <span className="font-semibold uppercase text-sm sm:text-base">{mode}</span>
                    {isDisabled && (
                      <span className="text-xs ml-1">🚫</span>
                    )}
                  </motion.label>
                );
              })}
            </div>
          </div>
        </div>

        {/* Pincode Inputs */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-4 sm:mb-6">
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
              Pickup Pincode <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <span className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 text-gray-400 text-base sm:text-lg">
                📍
              </span>
              <input
                type="text"
                name="pickupPincode"
                placeholder="e.g., 110001"
                value={formData.pickupPincode}
                onChange={handleInputChange}
                maxLength={6}
                className={`w-full border ${
                  fieldErrors.pickupPincode ? "border-red-500" : "border-gray-300"
                } rounded-lg sm:rounded-xl pl-10 sm:pl-12 pr-3 sm:pr-4 py-2.5 sm:py-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm sm:text-base`}
              />
            </div>
            {fieldErrors.pickupPincode && (
              <motion.p
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-red-500 text-xs sm:text-sm mt-1"
              >
                {fieldErrors.pickupPincode}
              </motion.p>
            )}
          </div>

          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
              Delivery Pincode <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <span className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 text-gray-400 text-base sm:text-lg">
                🎯
              </span>
              <input
                type="text"
                name="deliveryPincode"
                placeholder="e.g., 400001"
                value={formData.deliveryPincode}
                onChange={handleInputChange}
                maxLength={6}
                className={`w-full border ${
                  fieldErrors.deliveryPincode ? "border-red-500" : "border-gray-300"
                } rounded-lg sm:rounded-xl pl-10 sm:pl-12 pr-3 sm:pr-4 py-2.5 sm:py-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm sm:text-base`}
              />
            </div>
            {fieldErrors.deliveryPincode && (
              <motion.p
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-red-500 text-xs sm:text-sm mt-1"
              >
                {fieldErrors.deliveryPincode}
              </motion.p>
            )}
          </div>
        </div>

        {/* Dimensions */}
        <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-2 sm:mb-3">
          Package Dimensions <span className="text-red-500">*</span>
        </label>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-4 sm:mb-6">
          {["length", "breadth", "height", "weight"].map((field) => (
            <div key={field}>
              <div className="relative">
                <input
                  id={field}
                  type="number"
                  name={field}
                  min="0"
                  step="any"
                  placeholder={field.charAt(0).toUpperCase() + field.slice(1)}
                  value={formData[field]}
                  onChange={handleInputChange}
                  className={`w-full border ${
                    fieldErrors[field] ? "border-red-500" : "border-gray-300"
                  } rounded-t-lg sm:rounded-t-xl px-3 sm:px-4 py-2.5 sm:py-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm sm:text-base`}
                />
                <select
                  name={field}
                  value={units[field]}
                  onChange={handleUnitChange}
                  className="w-full border border-t-0 border-gray-300 rounded-b-lg sm:rounded-b-xl px-3 sm:px-4 py-1.5 sm:py-2 bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs sm:text-sm"
                >
                  {field === "weight" ? (
                    <>
                      <option value="gm">grams</option>
                      <option value="kg">kg</option>
                    </>
                  ) : (
                    <>
                      <option value="cm">cm</option>
                      <option value="inch">inch</option>
                    </>
                  )}
                </select>
              </div>
              {fieldErrors[field] && (
                <motion.p
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-red-500 text-xs mt-1"
                >
                  {fieldErrors[field]}
                </motion.p>
              )}
            </div>
          ))}
        </div>

        {/* Invoice Amount */}
        <div className="mb-4 sm:mb-6">
          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
            Invoice Amount <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <span className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 text-gray-400 text-base sm:text-lg">
              ₹
            </span>
            <input
              type="number"
              name="invoiceAmount"
              min="0"
              step="any"
              placeholder="Enter invoice amount"
              value={formData.invoiceAmount}
              onChange={handleInputChange}
              className={`w-full border ${
                fieldErrors.invoiceAmount ? "border-red-500" : "border-gray-300"
              } rounded-lg sm:rounded-xl pl-10 sm:pl-12 pr-3 sm:pr-4 py-2.5 sm:py-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm sm:text-base`}
            />
          </div>
          {fieldErrors.invoiceAmount && (
            <motion.p
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-red-500 text-xs sm:text-sm mt-1"
            >
              {fieldErrors.invoiceAmount}
            </motion.p>
          )}
        </div>

        {/* Success/Error Messages */}
        <AnimatePresence>
          {successMessage && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="mb-3 sm:mb-4 p-3 sm:p-4 bg-green-50 border border-green-200 rounded-lg sm:rounded-xl flex items-center gap-2 sm:gap-3"
            >
              <span className="text-green-600 text-lg sm:text-xl">✓</span>
              <span className="text-green-800 font-medium text-xs sm:text-sm">{successMessage}</span>
            </motion.div>
          )}

          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="mb-3 sm:mb-4 p-3 sm:p-4 bg-red-50 border border-red-200 rounded-lg sm:rounded-xl flex items-center gap-2 sm:gap-3"
            >
              <span className="text-red-600 text-lg sm:text-xl">⚠</span>
              <span className="text-red-800 font-medium text-xs sm:text-sm">{error}</span>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Calculate Button */}
        <motion.button
          onClick={handleCalculate}
          disabled={loading}
          whileHover={{ scale: loading ? 1 : 1.02 }}
          whileTap={{ scale: loading ? 1 : 0.98 }}
          className={`w-full ${
            loading
              ? "bg-gray-400 cursor-not-allowed"
              : "bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600"
          } text-white px-4 sm:px-6 py-3 sm:py-4 rounded-lg sm:rounded-xl font-bold text-sm sm:text-base lg:text-lg shadow-lg transition-all flex items-center justify-center gap-2 sm:gap-3 min-h-[52px]`}
        >
          {loading ? (
            <>
              <svg
                className="animate-spin h-5 w-5 sm:h-6 sm:w-6"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              <span className="text-sm sm:text-base">Calculating Rates...</span>
            </>
          ) : (
            <>
              <span className="text-sm sm:text-base">Calculate Shipping Rates</span>
              <span className="text-lg">🚀</span>
            </>
          )}
        </motion.button>
      </div>

      {/* Results Panel */}
      <AnimatePresence>
        {courierRates.length > 0 && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            transition={{ duration: 0.4 }}
            className="lg:col-span-1"
          >
            <div className="bg-gradient-to-br from-gray-50 to-blue-50 shadow-lg sm:shadow-xl rounded-xl sm:rounded-2xl p-4 sm:p-6 lg:sticky lg:top-6 border border-gray-200">
              {/* Header */}
              <div className="flex items-center justify-between mb-4 sm:mb-6">
                <div>
                  <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-0.5 sm:mb-1">
                    Shipping Rates
                  </h3>
                  <p className="text-xs sm:text-sm text-gray-600">
                    {courierRates.length} carriers available
                  </p>
                </div>
                <div className="bg-white p-1.5 sm:p-2 rounded-lg shadow-sm">
                  <span className="text-lg">📊</span>
                </div>
              </div>

              {/* Sort Options */}
              <div className="mb-3 sm:mb-4">
                <label className="block text-xs font-semibold text-gray-600 mb-1.5 sm:mb-2 uppercase">
                  Sort by
                </label>
                <div className="flex gap-1.5 sm:gap-2">
                  {[
                    { value: "price", label: "Price", icon: "💰" },
                    { value: "edd", label: "Speed", icon: "⚡" },
                    { value: "partner", label: "Partner", icon: "🏢" },
                  ].map((option) => (
                    <motion.button
                      key={option.value}
                      onClick={() => setSortBy(option.value)}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className={`flex-1 px-2 sm:px-3 py-1.5 sm:py-2 rounded-md sm:rounded-lg text-xs font-semibold transition-all ${
                        sortBy === option.value
                          ? "bg-blue-600 text-white shadow-md"
                          : "bg-white text-gray-600 hover:bg-gray-100"
                      }`}
                    >
                      <span className="mr-0.5 sm:mr-1">{option.icon}</span>
                      <span className="hidden xs:inline">{option.label}</span>
                    </motion.button>
                  ))}
                </div>
              </div>

              {/* Rates List */}
              <div className="space-y-2 sm:space-y-3 max-h-[400px] sm:max-h-[500px] lg:max-h-[600px] overflow-y-auto pr-1 sm:pr-2 custom-scrollbar">
                {sortedRates.map((rate, index) => {
                  const isLowest = index === 0 && sortBy === "price";
                  const isFastest = index === 0 && sortBy === "edd";

                  return (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.05 }}
                      className={`relative bg-white rounded-lg sm:rounded-xl p-3 sm:p-4 shadow-md hover:shadow-lg transition-all border-2 ${
                        isLowest || isFastest
                          ? "border-green-400 bg-green-50"
                          : "border-transparent"
                      }`}
                    >
                      {/* Best Deal Badge */}
                      {(isLowest || isFastest) && (
                        <div className="absolute -top-1.5 sm:-top-2 -right-1.5 sm:-right-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-xs font-bold shadow-lg flex items-center gap-0.5 sm:gap-1">
                          <span>⭐</span>
                          <span className="hidden xs:inline">{isLowest ? "Best Price" : "Fastest"}</span>
                        </div>
                      )}

                      {/* Partner Info */}
                      <div className="flex items-start justify-between mb-2 sm:mb-3">
                        <div className="flex items-center gap-2 sm:gap-3">
                          <div className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center font-bold text-xs sm:text-sm shadow-sm flex-shrink-0">
                            {rate.partner.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <div className="font-bold text-gray-900 text-xs sm:text-sm">
                              {rate.partner}
                            </div>
                            <div className="text-xs text-gray-500 uppercase font-semibold">
                              {rate.mode}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Price & Delivery */}
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-lg font-bold text-blue-600">
                            {rate.charges}
                          </div>
                          <div className="text-xs text-gray-500">Total Cost</div>
                        </div>
                        <div className="text-right">
                          <div className="flex items-center gap-0.5 sm:gap-1 text-base sm:text-lg font-bold text-orange-600">
                            <span>⏱</span>
                            <span>{rate.edd}</span>
                          </div>
                          <div className="text-xs text-gray-500">Delivery Time</div>
                        </div>
                      </div>

                      {/* Select Button */}
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className="w-full mt-2 sm:mt-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white py-1.5 sm:py-2 rounded-lg font-semibold text-xs sm:text-sm shadow-md transition-all"
                      >
                        Select Carrier
                      </motion.button>
                    </motion.div>
                  );
                })}
              </div>

              {/* Summary Stats */}
              <div className="mt-4 sm:mt-6 pt-3 sm:pt-4 border-t border-gray-200">
                <div className="grid grid-cols-2 gap-2 sm:gap-3">
                  <div className="bg-white p-2 sm:p-3 rounded-lg shadow-sm">
                    <div className="text-xs text-gray-500 mb-0.5 sm:mb-1">Lowest Rate</div>
                    <div className="font-bold text-green-600 text-sm sm:text-base">
                      {sortedRates.length > 0 && sortedRates[0].charges}
                    </div>
                  </div>
                  <div className="bg-white p-2 sm:p-3 rounded-lg shadow-sm">
                    <div className="text-xs text-gray-500 mb-0.5 sm:mb-1">Fastest Delivery</div>
                    <div className="font-bold text-orange-600 text-sm sm:text-base">
                      {sortedRates.length > 0 &&
                        [...sortedRates].sort(
                          (a, b) => parseInt(a.edd) - parseInt(b.edd)
                        )[0].edd}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Empty State */}
      {!loading && courierRates.length === 0 && (
        <div className="lg:col-span-1 flex items-center justify-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center p-6 sm:p-8"
          >
            <div className="text-3xl mb-3">📦</div>
            <h3 className="text-base sm:text-lg font-semibold text-gray-700 mb-1 sm:mb-2">
              Ready to Calculate
            </h3>
            <p className="text-gray-500 text-xs sm:text-sm">
              Fill in the details and click calculate to see shipping rates
            </p>
          </motion.div>
        </div>
      )}
    </motion.div>
  );
};

export default RateCalculator;
